import React from 'react';

import { LUMIO_MOBILE_ANIM } from '../../../../theme/tokens';
// Glow colours come from tokens.raw (RAW hex): they are baked into a data-URI SVG
// (waveTileImage), and var(--ows-…) theme tokens do NOT resolve inside a data URI.
import { LUMIO_MOBILE as LUMIO_RAW } from '../../../../theme/tokens.raw';
import { GlowBand, GlowLayer } from './LumioMobile.styles';
import { BOTTOM_WAVE_TILES, TOP_WAVE_TILES, waveTileImage } from './waves';

/**
 * The lumio-coloured glow field behind the chat — drifting, blurred wave layers
 * recreated from the Figma concept (node 8467:18951). Two bands hug the top and
 * bottom edges; each stacks a few brand-coloured waves drifting at different
 * speeds/directions (parallax), heavily blurred into soft light that screen-blends
 * over the near-black page.
 *
 * Each layer is a TILED data-URI wave drifted via `background-position` (NOT a
 * 400%-wide transform layer — that flickers on Safari; see waves.ts / the glow-waves
 * note in LumioMobile.styles). See `waves.ts` for the seamless-tile geometry.
 */

type LayerDef = {
  key: string;
  img: string;
  /** `up` glows hug the top edge (gradient strong at top); `down` hug the bottom. */
  facing: 'up' | 'down';
  durationMs: number;
  reverse?: boolean;
  blur: string;
  opacity: number;
  /** Negative animation-delay (ms) to de-sync layers in the loop. */
  delayMs?: number;
};

const TOP_LAYERS: LayerDef[] = [
  {
    key: 'top-cyan',
    img: waveTileImage(TOP_WAVE_TILES.cyan, LUMIO_RAW.glowCyan, 'up'),
    facing: 'up',
    durationMs: LUMIO_MOBILE_ANIM.waveSlowMs,
    blur: LUMIO_MOBILE_ANIM.glowBlur,
    opacity: 0.8,
  },
  {
    key: 'top-magenta',
    img: waveTileImage(TOP_WAVE_TILES.magenta, LUMIO_RAW.glowMagenta, 'up'),
    facing: 'up',
    durationMs: LUMIO_MOBILE_ANIM.waveMidMs,
    reverse: true,
    blur: LUMIO_MOBILE_ANIM.glowBlur,
    opacity: 0.55,
    delayMs: 9000,
  },
];

const BOTTOM_LAYERS: LayerDef[] = [
  {
    key: 'bot-violet',
    img: waveTileImage(BOTTOM_WAVE_TILES.violet, LUMIO_RAW.glowViolet, 'down'),
    facing: 'down',
    durationMs: LUMIO_MOBILE_ANIM.waveSlowMs,
    reverse: true,
    blur: LUMIO_MOBILE_ANIM.glowBlur,
    opacity: 0.62,
    delayMs: 4000,
  },
  {
    key: 'bot-indigo',
    img: waveTileImage(BOTTOM_WAVE_TILES.cyan, LUMIO_RAW.glowIndigo, 'down'),
    facing: 'down',
    durationMs: LUMIO_MOBILE_ANIM.waveSlowMs,
    blur: LUMIO_MOBILE_ANIM.glowBlur,
    opacity: 0.4,
    delayMs: 16000,
  },
  {
    key: 'bot-magenta',
    img: waveTileImage(BOTTOM_WAVE_TILES.magenta, LUMIO_RAW.glowMagenta, 'down'),
    facing: 'down',
    durationMs: LUMIO_MOBILE_ANIM.waveMidMs,
    blur: LUMIO_MOBILE_ANIM.glowBlur,
    opacity: 0.6,
    delayMs: 7000,
  },
  {
    key: 'bot-cyan',
    img: waveTileImage(BOTTOM_WAVE_TILES.cyan, LUMIO_RAW.glowCyan, 'down'),
    facing: 'down',
    durationMs: LUMIO_MOBILE_ANIM.waveFastMs,
    reverse: true,
    blur: LUMIO_MOBILE_ANIM.glowBlurSoft,
    opacity: 0.5,
    delayMs: 12000,
  },
];

const WaveLayer = ({ def }: { def: LayerDef }): React.ReactElement => (
  <GlowLayer
    $img={def.img}
    $dur={def.durationMs}
    $reverse={def.reverse}
    $blur={def.blur}
    $opacity={def.opacity}
    $delay={def.delayMs}
    $posY={def.facing === 'up' ? 'top' : 'bottom'}
    aria-hidden="true"
  />
);

export const GlowField = (): React.ReactElement => (
  <>
    {/* Band opacity halved (50% less bright); the bottom band is also cut to ~30%
     * of its former height (470 -> 141) so the lower glow reads as a slimmer hem. */}
    <GlowBand $edge="top" $h={248} $opacity={0.35}>
      {TOP_LAYERS.map((def) => (
        <WaveLayer key={def.key} def={def} />
      ))}
    </GlowBand>
    <GlowBand $edge="bottom" $h={141} $opacity={0.45}>
      {BOTTOM_LAYERS.map((def) => (
        <WaveLayer key={def.key} def={def} />
      ))}
    </GlowBand>
  </>
);

export default GlowField;
